import type { FC } from 'react';
import H3 from '@components/H3';
import { InlineEditableText } from '@components/InlineEditableText';
import InputText from '@components/InputText';
import { documentTitleClasses } from '@ContractBuilder/contract-builder-header/classes';
import { EntityField } from '@ContractBuilder/contract-builder-header/constants';
import type { OnSaveFn } from '@ContractBuilder/contract-builder-header/modules/entity-details/hooks/use-editable-field';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import type { EndorsementData, EntityData } from '@ContractBuilder/types';
import { isEndorsementData } from '@ContractBuilder/types';
import type { UIInputValue } from '@root/@types/types';
import { isTemplatePath } from '@root/src/utils/app-paths';

import { useEditableField } from '../hooks';

const getParsedTitle = ({ entity }: { entity: EntityData | EndorsementData }) => {
  if (isEndorsementData(entity)) {
    return entity.reference ?? entity.name;
  }

  return entity.name;
};

interface EditableNameFieldProps {
  entity: EntityData;
  hasOperationsDisabled: boolean;
  onSave: OnSaveFn;
}

export const EditableNameField: FC<EditableNameFieldProps> = ({ entity, hasOperationsDisabled, onSave }) => {
  const isTemplate = isTemplatePath();
  const hasUnsavedChanges = useDatapointsStore((state) => state.isDirty);
  const isDisabled = hasOperationsDisabled || hasUnsavedChanges;

  const { onCancel, onChange, onClick, onUpdate, state } = useEditableField({
    name: EntityField.Name,
    onSave,
    defaultValue: entity.name,
  });

  const parsedTitle = getParsedTitle({ entity });

  return (
    <InlineEditableText
      editor={
        <InputText
          value={state.value as UIInputValue}
          onChange={(event) => onChange(event.target.value)}
          name="submission_name"
          className="[&>div>input]:h-8"
        />
      }
      editMode={state.isEditing}
      disabled={isDisabled}
      className="min-w-0"
      display={
        <H3
          className={documentTitleClasses({
            isDisabled,
            isTemplate,
          })}
        >
          {parsedTitle}
        </H3>
      }
      onClick={onClick}
      onCancel={onCancel}
      onConfirm={onUpdate}
      confirmDisabled={!state.value || hasUnsavedChanges}
      tooltipContent={parsedTitle}
      withEditIcon={false}
    />
  );
};
